import { useEffect, useRef } from 'react'
import gsap from 'gsap'
import './WelcomeBridge.css'



const HEADLINE_PARTS = [
  { text: 'AVIATRICKS', accent: false },
  { text: 'AERO', accent: true },
  { text: 'LABS', accent: false },
]

const PILLARS = [
  { icon: '✦', title: 'Design', desc: 'Custom airframe · payload integration · performance optimization' },
  { icon: '◈', title: 'Hardware', desc: 'In-house PCB · flight controllers · power systems · sensors' },
  { icon: '⬡', title: 'Software', desc: 'Firmware · GCS · telemetry · automation · mission planning' },
]

export default function WelcomeBridge() {
  const wrapRef = useRef(null)
  const revealRef = useRef(null)
  const lettersRef = useRef([])
  const subtitleRef = useRef(null)
  const lineRef = useRef(null)
  const badgeRef = useRef(null)
  const pillarsRef = useRef([])   // card wrappers
  const iconsRef = useRef([])   // icons
  const titlesRef = useRef([])   // titles
  const descsRef = useRef([])   // descriptions
  const canvasRef = useRef(null)
  const rafRef = useRef(null)

  /* ── Particle canvas ── */
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    let progress = 0

    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight }
    resize()
    const ro = new ResizeObserver(resize)
    ro.observe(canvas)

    let particles = []
    const build = () => {
      particles = Array.from({ length: 110 }, (_, i) => {
        const angle = (i / 110) * Math.PI * 2
        const r = Math.random() * 55 + 15
        return {
          bx: canvas.width / 2 + Math.cos(angle) * r,
          by: canvas.height / 2 + Math.sin(angle) * r,
          ex: canvas.width / 2 + Math.cos(angle) * (270 + Math.random() * 330),
          ey: canvas.height / 2 + Math.sin(angle) * (270 + Math.random() * 330),
          size: Math.random() * 2.5 + 0.4,
          alpha: Math.random() * 0.55 + 0.28,
          hue: Math.random() * 40 + 260,
        }
      })
    }
    build()
    const roB = new ResizeObserver(build)
    roB.observe(canvas)

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      const p = progress
      particles.forEach(pt => {
        const x = pt.bx + (pt.ex - pt.bx) * p
        const y = pt.by + (pt.ey - pt.by) * p
        const a = pt.alpha * (1 - p * 1.1)
        if (a <= 0) return
        ctx.beginPath()
        ctx.arc(x, y, pt.size, 0, Math.PI * 2)
        ctx.fillStyle = `hsla(${pt.hue},${Math.floor(80 - 30 * p)}%,${Math.floor(60 + 10 * p)}%,${a})`
        ctx.fill()
      })
      rafRef.current = requestAnimationFrame(draw)
    }
    draw()
    canvas._setProgress = v => { progress = v }

    return () => {
      cancelAnimationFrame(rafRef.current)
      ro.disconnect()
      roB.disconnect()
    }
  }, [])

  /* ── GSAP scroll timeline ── */
  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: wrapRef.current,
          start: 'top top',
          end: '+=1600',
          scrub: 1.4,
          pin: true,
          pinSpacing: true,
          onUpdate(self) {
            if (canvasRef.current?._setProgress) canvasRef.current._setProgress(self.progress)
          },
        },
      })

      // Phase 0: badge
      tl.from(badgeRef.current, { opacity: 0, y: -14, duration: 0.2, ease: 'power2.out' }, 0)
      // Phase 1: line wipe
      tl.from(lineRef.current, { scaleX: 0, duration: 0.25, ease: 'power2.out', transformOrigin: 'left center' }, 0.05)
      // Phase 1b: letters cascade
      tl.from(lettersRef.current, { opacity: 0, y: 52, rotateX: -90, stagger: 0.016, duration: 0.28, ease: 'expo.out' }, 0.08)
      // Phase 2: subtitle
      tl.from(subtitleRef.current, { opacity: 0, y: 18, duration: 0.22, ease: 'power2.out' }, 0.28)
      // Phase 2b: pillar cards
      tl.from(pillarsRef.current, { opacity: 0, y: 30, stagger: 0.08, duration: 0.25, ease: 'power2.out' }, 0.34)

      // Phase 3: light reveal
      tl.fromTo(revealRef.current,
        { clipPath: 'circle(0% at 50% 50%)' },
        { clipPath: 'circle(150% at 50% 50%)', duration: 0.55, ease: 'power2.inOut' }, 0.42)

      // ── Phase 3b: everything flips dark on light lavender bg ──
      // Headline → very dark purple
      tl.to(lettersRef.current, { color: '#1e0a3c', duration: 0.25, ease: 'none' }, 0.56)
      // Subtitle → solid dark violet (NO more near-invisible)
      tl.to(subtitleRef.current, { color: '#3b0f6e', duration: 0.25, ease: 'none' }, 0.56)
      // Badge
      tl.to(badgeRef.current.querySelector?.('.wb-badge-text') || '.wb-badge-text',
        { color: '#6d28d9', duration: 0.2, ease: 'none' }, 0.56)
      // Line
      tl.to(lineRef.current, { background: 'linear-gradient(90deg,#6d28d9,#a855f7)', duration: 0.2, ease: 'none' }, 0.56)

      // Cards — solid, visible border + background
      tl.to(pillarsRef.current, { borderColor: 'rgba(109,40,217,0.45)', background: 'rgba(109,40,217,0.1)', duration: 0.2, ease: 'none' }, 0.56)
      // Icons — vivid purple
      tl.to(iconsRef.current, { color: '#6d28d9', opacity: 1, duration: 0.2, ease: 'none' }, 0.56)
      // Titles — near-black, clearly readable
      tl.to(titlesRef.current, { color: '#1e0a3c', duration: 0.2, ease: 'none' }, 0.56)
      // Descriptions — dark enough to read
      tl.to(descsRef.current, { color: 'rgba(45,10,90,0.8)', duration: 0.2, ease: 'none' }, 0.56)

    }, wrapRef)

    return () => ctx.revert()
  }, [])

  let letterIdx = 0

  return (
    <div ref={wrapRef} className="wb-wrap">
      <div className="wb-dark" />
      <div ref={revealRef} className="wb-light" />
      <canvas ref={canvasRef} className="wb-canvas" />

      <div className="wb-content">

        {/* Brand badge */}
        <div ref={badgeRef} className="wb-badge">
          <span className="wb-badge-line" />
          <span className="wb-badge-text">End-to-End UAV Solutions</span>
          <span className="wb-badge-line right" />
        </div>

        {/* Accent divider */}
        <div ref={lineRef} className="wb-line" />

        {/* Headline */}
        <h1 className="wb-headline" aria-label={HEADLINE_PARTS.map(p => p.text).join(' ')}>
          {HEADLINE_PARTS.map((part, wi) => (
            <span key={wi} className="wb-word">
              {part.text.split('').map(l => {
                const idx = letterIdx++
                return (
                  <span
                    key={idx}
                    ref={el => { lettersRef.current[idx] = el }}
                    className={`wb-letter${part.accent ? ' wb-headline-accent' : ''}`}
                  >{l}</span>
                )
              })}
              {wi < HEADLINE_PARTS.length - 1 && <span className="wb-space">&nbsp;</span>}
            </span>
          ))}
        </h1>

        {/* Subtitle */}
        <p ref={subtitleRef} className="wb-sub">
          Full-stack drone manufacturing · Aeronautical design + Embedded hardware + Custom flight software<br />
          Agriculture · Mapping · Inspection · FPV · Enterprise
        </p>

        {/* Pillar cards */}
        <div className="wb-pillars">
          {PILLARS.map((p, i) => (
            <div
              key={i}
              ref={el => { pillarsRef.current[i] = el }}
              className="wb-pillar"
            >
              <span
                ref={el => { iconsRef.current[i] = el }}
                className="wb-pillar-icon"
              >{p.icon}</span>
              <span
                ref={el => { titlesRef.current[i] = el }}
                className="wb-pillar-title"
              >{p.title}</span>
              <span
                ref={el => { descsRef.current[i] = el }}
                className="wb-pillar-desc"
              >{p.desc}</span>
            </div>
          ))}
        </div>

      </div>
    </div>
  )
}